import {
    type OnDataOptions,
    useApolloClient,
    useSubscription,
} from '@apollo/client';
import { createElement, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET_ALL_PROJECTS } from '@api/queries/projects';
import { USER_EVENTS } from '@api/subscriptions/userEvents';
import { useToast } from '@contexts/GlobalUiContext';
import {
    OperationStatus,
    type UserEvent,
    UserEventType,
    type UserEventsSubscription,
} from '@gql/graphql';
import { CopyCompletedToastContent } from '@pages/projects/_utils/components/projectCopyForm/CopyCompletedToastContent';
import { CopyProjectFailedToastContent } from '@pages/projects/_utils/components/projectCopyForm/CopyProjectFailedToastContent';
import { CopyProjectInProgressToastContent } from '@pages/projects/_utils/components/projectCopyForm/CopyProjectInProgressToastContent';
import { COPY_PROJECT_TOAST_CLASS } from '@pages/projects/_utils/components/projectCopyForm/CopyProjectToastShell';
import {
    projectCopyOperationToastId,
    projectCopySuccessToastId,
} from '@pages/projects/_utils/components/projectCopyForm/project-copy-toasts';

const COPY_IN_PROGRESS_TOAST_LIFE_MS = 3000;

export default function useUserEventsSubscription(): void {
    const navigate = useNavigate();
    const { pushToast, dismissToast } = useToast();
    const client = useApolloClient();

    const handleProjectCopyEvent = ({
        status,
        resourceID,
        message,
    }: UserEvent): void => {
        const operationToastId = projectCopyOperationToastId(resourceID);

        switch (status) {
            case OperationStatus.Pending:
            case OperationStatus.InProgress:
                pushToast({
                    id: operationToastId,
                    severity: 'info',
                    life: COPY_IN_PROGRESS_TOAST_LIFE_MS,
                    closable: true,
                    className: COPY_PROJECT_TOAST_CLASS,
                    content: createElement(CopyProjectInProgressToastContent, {
                        projectID: resourceID,
                    }),
                });
                break;
            case OperationStatus.Completed: {
                const successToastId = projectCopySuccessToastId(resourceID);
                pushToast({
                    id: successToastId,
                    severity: 'success',
                    sticky: true,
                    closable: true,
                    className: COPY_PROJECT_TOAST_CLASS,
                    content: createElement(CopyCompletedToastContent, {
                        projectID: resourceID,
                        onOpen: () => {
                            dismissToast(successToastId);
                            navigate(`/projects/${resourceID}`);
                        },
                    }),
                });
                void client.refetchQueries({ include: [GET_ALL_PROJECTS] });
                break;
            }
            case OperationStatus.Failed:
                dismissToast(operationToastId);
                pushToast({
                    id: operationToastId,
                    severity: 'error',
                    sticky: true,
                    closable: true,
                    className: COPY_PROJECT_TOAST_CLASS,
                    content: createElement(CopyProjectFailedToastContent, {
                        projectID: resourceID,
                        errorCode: message ?? null,
                    }),
                });
                break;
        }
    };

    const handlerRef = useRef(handleProjectCopyEvent);
    handlerRef.current = handleProjectCopyEvent;

    const onData = useCallback(
        ({ data: { data } }: OnDataOptions<UserEventsSubscription>) => {
            if (!data) return;
            const event = data.userEvents;
            if (event.type === UserEventType.ProjectCopy) {
                handlerRef.current(event);
            }
        },
        [],
    );

    useSubscription(USER_EVENTS, { onData });
}
